import { useEffect } from 'react'

import { format } from 'date-fns'
import { useFormContext, type FieldPath, type FieldValues, type PathValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Render } from '@/components/render-if'
import { ComboboxField } from '@/components/ui/combobox'
import { CardEtapaInicial } from '@/features/proposta/brasil/components/card-etapa-inicial'
import { usePropostaContext } from '@/features/proposta/brasil/context/proposta-context'
import { PlantasBloco } from '@/features/proposta/brasil/domain/planta-bloco'
import { useSuspensePlantasBloco } from '@/features/proposta/brasil/hooks'

export const CardPlanta = <TFieldValues extends FieldValues = FieldValues>() => {
  const { t } = useTranslation('proposta')
  const { control, watch, setValue } = useFormContext<TFieldValues>()
  const { idExternoBlocoPath, dataMarcacaoAlvenariaPath } = usePropostaContext()

  const dataMarcacaoAlvenaria = watch(dataMarcacaoAlvenariaPath as FieldPath<TFieldValues>)
  const idExternoBlocoUnidade = watch(idExternoBlocoPath as FieldPath<TFieldValues>)
  const { data: plantas } = useSuspensePlantasBloco(idExternoBlocoUnidade)
  const plantaSelecionada = watch('informacoesIniciais.planta' as FieldPath<TFieldValues>)

  const variant = plantaSelecionada.id ? 'completed' : 'default'
  const isDataAlvenariaAnteriorHoje = new PlantasBloco(plantas).isDataAlvenariaAnteriorHoje(dataMarcacaoAlvenaria)

  useEffect(() => {
    const plantasBloco = new PlantasBloco(plantas)
    if (isDataAlvenariaAnteriorHoje && !plantaSelecionada.id) {
      const primeiraPlanta = plantasBloco.getPrimeiraPlantaDoBloco()
      setValue(
        `informacoesIniciais.planta` as FieldPath<TFieldValues>,
        primeiraPlanta as PathValue<TFieldValues, FieldPath<TFieldValues>>,
      )
    }
  }, [dataMarcacaoAlvenaria, isDataAlvenariaAnteriorHoje, plantaSelecionada.id, plantas, setValue])
  return (
    <CardEtapaInicial variant={variant}>
      <CardEtapaInicial.Content
        label={t('informacoes_iniciais.cards.planta.titulo')}
        description={
          isDataAlvenariaAnteriorHoje
            ? t('informacoes_iniciais.cards.planta.tipo')
            : t('informacoes_iniciais.cards.planta.selecione')
        }
      >
        <Render.If condition={dataMarcacaoAlvenaria}>
          {(data) => (
            <p className="text-xs">
              <span className="font-semibold">{t('informacoes_iniciais.cards.planta.marcacao_alvenaria')}</span>
              {` ${format(data, 'dd/MM/yyyy')}`}
            </p>
          )}
        </Render.If>
      </CardEtapaInicial.Content>
      <CardEtapaInicial.Action>
        <ComboboxField
          data-testid="selecao-planta"
          name={`informacoesIniciais.planta` as FieldPath<TFieldValues>}
          valueMode="object"
          objectProperties={['id', 'descricao']}
          control={control}
          placeholder={t('informacoes_iniciais.cards.selecione')}
          options={plantas}
          labelProperty="descricao"
          valueProperty="id"
          initialValue={{ id: '', descricao: '' }}
          disableFilter={isDataAlvenariaAnteriorHoje}
        />
      </CardEtapaInicial.Action>
    </CardEtapaInicial>
  )
}
